import React, { useState } from 'react';
import { Save, Edit2, X } from 'lucide-react';
import { professionalClientService } from '../../../services/professionalClientService';

const ClientInfoTab = ({ client, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    companyName: client?.companyName || '',
    contactName: client?.contactName || '',
    email: client?.email || '',
    phone: client?.phone || '',
    clientType: client?.clientType || 'other',
    siret: client?.siret || '',
    address: client?.address || '',
    assignedAdvisor: client?.assignedAdvisor || '',
    dedicatedLineNumber: client?.dedicatedLineNumber || '',
    negotiatedRate: client?.negotiatedRate || '',
    internalNotes: client?.internalNotes || '',
    accountStatus: client?.accountStatus || 'active'
  });

  const handleSave = async () => {
    try {
      setSaving(true);
      await professionalClientService?.updateClient(client?.id, formData);
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Erreur lors de la mise à jour du client');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      companyName: client?.companyName || '',
      contactName: client?.contactName || '',
      email: client?.email || '',
      phone: client?.phone || '',
      clientType: client?.clientType || 'other',
      siret: client?.siret || '',
      address: client?.address || '',
      assignedAdvisor: client?.assignedAdvisor || '',
      dedicatedLineNumber: client?.dedicatedLineNumber || '',
      negotiatedRate: client?.negotiatedRate || '',
      internalNotes: client?.internalNotes || '',
      accountStatus: client?.accountStatus || 'active'
    });
    setIsEditing(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Informations du client</h2>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            Modifier
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Sauvegarder'}
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Company Information */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Nom de l'entreprise *</label>
          <input
            type="text"
            value={formData?.companyName}
            onChange={(e) => setFormData({ ...formData, companyName: e?.target?.value })}
            disabled={!isEditing}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Contact principal *</label>
          <input
            type="text"
            value={formData?.contactName}
            onChange={(e) => setFormData({ ...formData, contactName: e?.target?.value })}
            disabled={!isEditing}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
          <input
            type="email"
            value={formData?.email}
            onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
            disabled={!isEditing}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
          <input
            type="tel"
            value={formData?.phone}
            onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
            disabled={!isEditing}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Type de client</label>
          <select
            value={formData?.clientType}
            onChange={(e) => setFormData({ ...formData, clientType: e?.target?.value })}
            disabled={!isEditing}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
          >
            <option value="merchant">Commerçant</option>
            <option value="association">Association</option>
            <option value="company">Entreprise</option>
            <option value="moving">Déménagement</option>
            <option value="other">Autre</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SIRET</label>
          <input
            type="text"
            value={formData?.siret}
            onChange={(e) => setFormData({ ...formData, siret: e?.target?.value })}
            disabled={!isEditing}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
          <input
            type="text"
            value={formData?.address}
            onChange={(e) => setFormData({ ...formData, address: e?.target?.value })}
            disabled={!isEditing}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Conseiller pro attribué</label>
          <input
            type="text"
            value={formData?.assignedAdvisor}
            onChange={(e) => setFormData({ ...formData, assignedAdvisor: e?.target?.value })}
            disabled={!isEditing}
            placeholder="Nom du conseiller ADT Express"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Numéro de ligne pro dédiée</label>
          <input
            type="tel"
            value={formData?.dedicatedLineNumber}
            onChange={(e) => setFormData({ ...formData, dedicatedLineNumber: e?.target?.value })}
            disabled={!isEditing}
            placeholder="+33 6 XX XX XX XX"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tarif négocié (€)</label>
          <input
            type="number"
            step="0.01"
            value={formData?.negotiatedRate}
            onChange={(e) => setFormData({ ...formData, negotiatedRate: e?.target?.value })}
            disabled={!isEditing}
            placeholder="Tarif spécial si différent du standard"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Statut du compte</label>
          <select
            value={formData?.accountStatus}
            onChange={(e) => setFormData({ ...formData, accountStatus: e?.target?.value })}
            disabled={!isEditing}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
          >
            <option value="active">Actif</option>
            <option value="pending">En attente</option>
            <option value="inactive">Inactif</option>
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Notes internes</label>
          <textarea
            value={formData?.internalNotes}
            onChange={(e) => setFormData({ ...formData, internalNotes: e?.target?.value })}
            disabled={!isEditing}
            rows={4}
            placeholder="Notes réservées à l'équipe ADT Express"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
          />
        </div>
      </div>
    </div>
  );
};

export default ClientInfoTab;